// ANGEL FLIGHT — CameraRig: trauma screenshake + hitstop on the Three camera.
//
// The sensor camera is fixed (you ARE the gun camera). This adds FEEL: a
// trauma-model shake (offset = trauma² · maxOffset, decays per second) applied as
// a small positional jitter, plus a brief sim-freeze on kills. Owns the `frozen`
// flag the GameLoop reads to gate the SIM during hitstop. Honours the
// accessibility toggles. Ported from the Phaser CameraRig (same trauma math).

import * as THREE from 'three';
import { BALANCE } from '../config/balance';
import { clamp } from '../core/mathx';
import type { GameContext, GameSystem } from '../systems/System';

export class CameraRig implements GameSystem {
  private ctx!: GameContext;
  private camera!: THREE.PerspectiveCamera;
  private readonly basePos = new THREE.Vector3();
  private trauma = 0;
  private shakeEnabled = true;
  private hitstopEnabled = true;
  private hitstopLeftMs = 0;
  /** Read by the GameLoop to freeze the SIM during hitstop. */
  frozen = false;

  private readonly onShake = (p: { trauma: number }): void => {
    if (this.shakeEnabled) this.trauma = clamp(this.trauma + p.trauma, 0, BALANCE.juice.shakeMax);
  };
  private readonly onHitstop = (p: { ms: number }): void => {
    if (this.hitstopEnabled) this.hitstopLeftMs = Math.max(this.hitstopLeftMs, p.ms);
  };

  init(ctx: GameContext): void {
    this.ctx = ctx;
    // Reach the concrete camera through the view seam.
    this.camera = (ctx.view as unknown as { camera: THREE.PerspectiveCamera }).camera;
    this.basePos.copy(this.camera.position);
    ctx.bus.on('fx:shake', this.onShake);
    ctx.bus.on('fx:hitstop', this.onHitstop);
  }

  configure(opts: { screenshake?: boolean; hitstop?: boolean }): void {
    this.shakeEnabled = opts.screenshake ?? true;
    this.hitstopEnabled = opts.hitstop ?? true;
  }

  update(dt: number): void {
    this.frozen = this.hitstopLeftMs > 0;
    if (this.hitstopLeftMs > 0) this.hitstopLeftMs -= dt;

    const dtSec = dt / 1000;
    if (this.trauma > 0) this.trauma = Math.max(0, this.trauma - BALANCE.juice.shakeDecay * dtSec);

    if (this.shakeEnabled && this.trauma > 0) {
      // World-units shake scaled from the px offset (≈ /60 keeps it subtle at
      // this camera distance). Jitter position around the fixed base.
      // RENDER rng stream only — shake draws per FRAME, so pulling the sim
      // stream here would make sim outcomes frame-rate-dependent.
      const amt = (this.trauma * this.trauma * BALANCE.juice.shakeMaxOffset) / 60;
      this.camera.position.set(
        this.basePos.x + (this.ctx.renderRng.next() * 2 - 1) * amt,
        this.basePos.y + (this.ctx.renderRng.next() * 2 - 1) * amt,
        this.basePos.z + (this.ctx.renderRng.next() * 2 - 1) * amt * 0.5,
      );
    } else {
      this.camera.position.copy(this.basePos);
    }
  }

  destroy(): void {
    this.ctx?.bus.off('fx:shake', this.onShake);
    this.ctx?.bus.off('fx:hitstop', this.onHitstop);
    this.camera?.position.copy(this.basePos);
  }
}
